import { useEffect } from 'react';
import styled from 'styled-components';

import { Button } from '../../../../cdl/Button/Button';
import { Callout } from '../../../../cdl/Callout/Callout';
import { StatePlaceholder } from '../../../../cdl/StatePlaceholder/StatePlaceholder';
import { Text } from '../../../../cdl/Text/Text';
import { IntelligenceEventType } from '../../../../common/api/clients/intelligence.api';
import { translate } from '../../../../common/helpers/translate.helper';
import { Characters } from '../../../../common/helpers/unicode.helper';
import { IconAlertTriangle } from '../../../../common/icons/cdl/AlertTriangle';
import { IconInfoCircle } from '../../../../common/icons/cdl/InfoCircle';
import { IconSparkle } from '../../../../common/icons/cdl/Sparkle';
import { LoadingBar } from '../../../../common/LoadingBar/LoadingBar';
import { Box } from '../../../../common/ui/Box';
import { IntelligenceReasoning } from '../../../create-enquiry/components/intelligence/IntelligenceReasoning';
import { useOfferSuggestionsStream } from '../../../create-enquiry/hooks/useOfferSuggestionsStream';
import { OfferReviewProgressIndicator } from '../../components/intelligence/OfferReviewProgressIndicator';

const Container = styled(Box).attrs({
    overflowY: 'auto',
    paddingX: 24,
    paddingBottom: 24,
    display: 'flex',
    flexDirection: 'column',
    gap: 8,
})``;

interface OfferReviewDrawerContentProps {
    orderId: string;
}

export const OfferReviewDrawerContent = ({ orderId }: OfferReviewDrawerContentProps) => {
    const streamQuery = useOfferSuggestionsStream(orderId);

    useEffect(() => {
        if (!streamQuery.isLoading && !streamQuery.isError && !streamQuery.finalResult && !streamQuery.events.length) {
            streamQuery.startStream();
        }
    }, [streamQuery]);

    const latestLLMEvent = streamQuery.events
        .filter((event) => event.event === IntelligenceEventType.LLM_UPDATE)
        .at(-1);

    if (streamQuery.isLoading) {
        return (
            <Container>
                <Box
                    display="grid"
                    gridTemplateColumns="20px auto"
                    alignItems="center"
                    gap={8}
                    padding={16}
                    color="special.foreground"
                    borderRadius={8}
                    border="1px solid"
                    borderColor="border.subtle"
                    backgroundColor="background.subtle"
                    marginBottom={8}
                >
                    <IconSparkle />
                    <Text variant="small" fontWeight={600}>
                        {translate('closelinkInsights.reviewInProgress')}
                        {Characters.HORIZONTAL_ELLIPSIS}
                    </Text>
                    <Box gridColumn="1/3">
                        <LoadingBar color="special.emphasis" />
                    </Box>
                </Box>

                <OfferReviewProgressIndicator events={streamQuery.events} />

                {latestLLMEvent ? <IntelligenceReasoning markdownReasoning={latestLLMEvent.data} /> : null}
            </Container>
        );
    }

    if (streamQuery.isError) {
        return (
            <Container>
                <Box display="flex" justifyContent="center" marginTop="124px">
                    <StatePlaceholder
                        title={translate('global.genericError.title')}
                        description={translate('global.genericError.description')}
                        Icon={IconAlertTriangle}
                        action={
                            <Button onClick={() => streamQuery.startStream()}>
                                {translate('closelinkInsights.tryAgain')}
                            </Button>
                        }
                    />
                </Box>
            </Container>
        );
    }

    if (streamQuery.events.length && !streamQuery.isLoading && !streamQuery.isSuccess) {
        return (
            <Container>
                <Callout
                    variant="accent"
                    heading={translate('closelinkInsights.noSuitableOffer')}
                    description={translate('closelinkInsights.noSuitableOffer.description')}
                    Icon={IconInfoCircle}
                />
            </Container>
        );
    }

    return (
        <Container>
            <IntelligenceReasoning
                markdownReasoning={streamQuery.finalResult?.detailedMarkdownMessage}
                textReasoning={streamQuery.finalResult?.message}
            />
        </Container>
    );
};
